import React from "react";
import {Box, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";
import {SettingsSearchInput, SettingsSearchResults} from "./SettingsSearchPrimitives";
import {useSettingsSearch} from "./useSettingsSearch";
import type {SettingsSearchProps, SettingsSearchSection} from "./settingsSearch.types";

const QUICK_ACCESS_ITEM_IDS = ['services', 'folios', 'profiles', 'guestAppUsers', 'reservations', 'resources']
const QUICK_ACTION_ITEM_IDS = ['services', 'profiles', 'reservations', 'folios']
const LANDING_CONTENT_MAX_WIDTH = 840

const SettingsLandingSearch = (searchProps: SettingsSearchProps) => {
    const {t} = useTranslation()
    const search = useSettingsSearch({
        ...searchProps,
        idleGoToItemIds: QUICK_ACCESS_ITEM_IDS,
        idleCreateItemIds: QUICK_ACTION_ITEM_IDS,
    })
    const sections = React.useMemo<SettingsSearchSection[]>(() => [
        {title: 'Recent', results: search.displayedRecentResults},
        {title: search.isIdle ? 'Quick access' : 'Go to', results: search.goToResults},
        {title: 'Find', results: search.findResults},
        {title: 'Results', results: search.searchResults},
        {title: search.isIdle ? 'Quick actions' : 'Create', results: search.createResults},
    ], [
        search.createResults,
        search.displayedRecentResults,
        search.findResults,
        search.goToResults,
        search.isIdle,
        search.searchResults,
    ])

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                height: '100%',
                minHeight: 0,
                overflow: 'hidden',
            }}
        >
            <Box sx={{width: '100%', maxWidth: LANDING_CONTENT_MAX_WIDTH, mx: 'auto', flexShrink: 0}}>
                <Box sx={{px: 1.25, pb: 1.5}}>
                    <Typography variant="h4" sx={{fontWeight: 700}}>
                        {t('Settings')}
                    </Typography>
                    <Typography variant="body2" sx={{color: 'text.secondary', mt: 0.5}}>
                        {t('Search settings, records, and available actions.')}
                    </Typography>
                </Box>

                <Box sx={{px: 1.25, pb: 0.5}}>
                    <SettingsSearchInput search={search}/>
                </Box>
            </Box>

            <Box sx={{display: 'flex', flex: 1, width: '100%', minHeight: 0, overflow: 'hidden'}}>
                <SettingsSearchResults
                    search={search}
                    sections={sections}
                    contentMaxWidth={LANDING_CONTENT_MAX_WIDTH}
                />
            </Box>
        </Box>
    )
}

export default SettingsLandingSearch
